import { BaseQueryFn, EndpointBuilder } from '@reduxjs/toolkit/query';
import { BaseQueryParams } from 'types/base-query-params';
import { PaginatedResponse } from 'types/dtos';
import { ResponseProductDto } from '../types/dtos';
import { constructQueryParams } from 'utils/query-params-builder';
import { APP_ENDPOINTS } from 'app/endpoints';
import { handleDuplicates, removeUndefined } from 'utils/array-helpers';
import { formatDate } from 'utils/date-helpers';
import { formatPrice } from 'utils/price-helpers';
import { Document } from 'services/documents/types/dtos';
import { checkError, fetchResponse } from 'utils/api-helpers';
import { ResponseProductCategoryDto } from 'features/product-category/types/dtos';
import { createEmptyData } from 'constants/emptyData';
import { PRODUCT_STATUS } from '../constants/status';

export type ProductsWithJoinQueryParams = BaseQueryParams & {
    placeId?: string;
};

export default (builder: EndpointBuilder<BaseQueryFn, string, string>) =>
    builder.query<PaginatedResponse<ResponseProductDto>, ProductsWithJoinQueryParams>({
        async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
            const { placeId, ...cleanParams } = _arg;

            if (!placeId) {
                return {
                    data: createEmptyData<ResponseProductDto>(),
                };
            }

            const productsResponse = await fetchWithBQ(
                constructQueryParams(`${APP_ENDPOINTS.PRODUCTS}/place/${_arg.placeId}`, cleanParams),
            );
            const productsError = checkError(productsResponse);
            if (productsError) return productsError;
            const products = productsResponse.data as PaginatedResponse<ResponseProductDto>;

            const productCategoryIds = removeUndefined(
                handleDuplicates(products.content.map((productCategory) => productCategory.productCategoryId)),
            );
            const productCategoriesResponse = await fetchResponse(
                APP_ENDPOINTS.PRODUCT_CATEGORIES,
                productCategoryIds,
                fetchWithBQ,
            );
            const productCategoryError = checkError(productCategoriesResponse);
            if (productCategoryError) return productCategoryError;
            const productCategories = productCategoriesResponse.data as PaginatedResponse<ResponseProductCategoryDto>;

            let documents: PaginatedResponse<Document>;
            const documentIds = handleDuplicates(
                removeUndefined(products.content.map((document) => document.documentId)),
            );
            if (documentIds.length > 0) {
                const documentResponse = await fetchResponse(APP_ENDPOINTS.DOCUMENTS, documentIds, fetchWithBQ);
                const documentError = checkError(documentResponse);
                if (documentError) return documentError;
                documents = documentResponse.data as PaginatedResponse<Document>;
            }

            products.content = products.content.map((product) => {
                const productCategoryName = productCategories.content.find((productCategory) => {
                    return productCategory.id === product.productCategoryId;
                })?.name;

                const isAvailable = product.isAvailable
                    ? `${PRODUCT_STATUS.AVAILABLE}`
                    : `${PRODUCT_STATUS.UNAVAILABLE}`;

                const createdAt = formatDate(product.createdAt);

                const price = formatPrice(product.price);

                const discountedPrice =
                    !isNaN(Number(product.discountedPrice)) && product.discountedPrice !== '0'
                        ? formatPrice(product.discountedPrice)
                        : '';

                const filePath = documents?.content.find((document) => {
                    return document.id === product.documentId;
                })?.filePaths;

                const imageUrl = filePath && JSON.parse(filePath).small;

                return {
                    ...product,
                    placeId: placeId,
                    createdAt,
                    isAvailable,
                    productCategoryName,
                    price,
                    discountedPrice,
                    imageUrl,
                };
            });

            return {
                data: products,
            };
        },
        providesTags: [{ type: 'products-with-join', id: 'GET-JOIN' }],
    });
